import { Group, HistoryActionType, Pouch, PouchLayerValue, Sheet, SheetHistoryEntry, SheetLayer } from '../types';
import { getAppCopy } from '../i18n';
import { v4 as uuidv4 } from 'uuid';
import { roundPouchValue } from '../utils/pouchValueRounding';
import {
  calculateSheetTotal,
  applyTimeEffectiveGtpBake,
  getTimeLayerEffectiveMinutes,
  getTimePouchEffectiveMinutes,
} from './BalancingPouchLogic';
import { timePouchStateFromEffectiveMinutes } from '../utils/timePouchState';
import {
  calculateGroupSubtotal,
  computeGroupInclusiveGtpMergeFactor,
  findGroupInclusiveGtps,
  getGroupTotalPouchDisplayBase,
  getInclusiveGtpSheetPrimaryPouchId,
  renderGroupInclusiveGtp,
} from './GroupLogic';

const MAX_AGGREGATION_VALUE = 9999999999;

export const DEFAULT_LAYER_ID = 'default';

/** レイヤー列見出し行（テーブル選択・編集用） */
export const LAYER_HEADER_ROW_INDEX = -1;

export function isLayerHeaderRow(rowIndex: number): boolean {
  return rowIndex === LAYER_HEADER_ROW_INDEX;
}

export function createDefaultLayer(name = ''): SheetLayer {
  return {
    id: DEFAULT_LAYER_ID,
    name,
    order: 0,
    primary: true,
    kind: 'number',
  };
}

export function getSortedLayers(sheet: Sheet): SheetLayer[] {
  const layers = sheet.layers?.length ? [...sheet.layers] : [createDefaultLayer()];
  return layers.sort((a, b) => a.order - b.order);
}

export function sheetLayerIsTimeKind(layer: Pick<SheetLayer, 'kind' | 'unit'>): boolean {
  return layer.kind === 'time' || layer.unit === 'h' || layer.unit === 'min';
}

export function getPrimaryTimeLayerId(sheet: Sheet): string | undefined {
  return getSortedLayers(sheet).find(l => sheetLayerIsTimeKind(l))?.id;
}

export function getPrimaryLayer(sheet: Sheet): SheetLayer {
  const sorted = getSortedLayers(sheet);
  return sorted.find(l => l.primary) ?? sorted[0];
}

export function resolveActiveLayerId(sheet: Sheet): string {
  const sorted = getSortedLayers(sheet);
  if (sheet.activeLayerId && sorted.some(l => l.id === sheet.activeLayerId)) {
    return sheet.activeLayerId;
  }
  return getPrimaryLayer(sheet).id;
}

export function getActiveLayer(sheet: Sheet): SheetLayer {
  const id = resolveActiveLayerId(sheet);
  return getSortedLayers(sheet).find(l => l.id === id) ?? getPrimaryLayer(sheet);
}

export function getLayerById(sheet: Sheet, layerId: string): SheetLayer | undefined {
  return getSortedLayers(sheet).find(l => l.id === layerId);
}

export function getLayerValue(pouch: Pouch, layerId: string): number {
  return pouch.layerValues?.[layerId]?.value ?? 0;
}

export function getLayerEntry(pouch: Pouch, layerId: string): PouchLayerValue {
  return pouch.layerValues?.[layerId] ?? { value: 0 };
}

export function sheetHasMultipleLayers(sheet: Sheet | undefined): boolean {
  return (sheet?.layers?.length ?? 0) > 1;
}

export function pouchHasLayerValues(pouch: Pouch): boolean {
  return pouch.layerValues != null && Object.keys(pouch.layerValues).length > 0;
}

/** 多層、または単層でも layerValues を持つパウチは layerValues を正とする */
export function pouchUsesLayerValues(sheet: Sheet | undefined, pouch: Pouch): boolean {
  if (!sheet?.layers?.length) return false;
  return sheetHasMultipleLayers(sheet) || pouchHasLayerValues(pouch);
}

/** 表示・編集用 multiplier（時間レイヤーは layerValues、legacy 単層は top-level にフォールバック） */
export function getPouchEffectiveMultiplierForSheet(
  pouch: Pouch,
  sheet: Sheet | undefined,
  layerId?: string,
): number {
  const lid = layerId ?? (sheet ? resolveActiveLayerId(sheet) : DEFAULT_LAYER_ID);
  if (!sheet?.layers?.length) return pouch.multiplier;
  if (getPouchLayerIsTime(pouch, lid, sheet) || pouchUsesLayerValues(sheet, pouch)) {
    return getPouchLayerMultiplier(pouch, lid);
  }
  return pouch.multiplier;
}

/** 時間パウチの H:MM 表示用分数（value×multiplier。layerValues 経路を含む） */
export function getPouchTimeDisplayMinutes(
  pouch: Pouch,
  sheet: Sheet | undefined,
  layerId?: string,
): number {
  const lid = layerId ?? (sheet ? resolveActiveLayerId(sheet) : DEFAULT_LAYER_ID);
  const mult = getPouchEffectiveMultiplierForSheet(pouch, sheet, lid);
  const value = pouchUsesLayerValues(sheet, pouch) ? getLayerValue(pouch, lid) : pouch.value;
  return getTimePouchEffectiveMinutes({ value, multiplier: mult, isTime: true }, mult);
}

export function getPouchLayerExpressionFields(pouch: Pouch, layerId: string) {
  const entry = getLayerEntry(pouch, layerId);
  return {
    lastExpression: entry.lastExpression ?? pouch.lastExpression,
    valueWhenLastExpression: entry.valueWhenLastExpression ?? pouch.valueWhenLastExpression,
    multiplyWhenLastExpression: pouch.multiplyWhenLastExpression,
    roundUnitWhenLastExpression:
      entry.roundUnitWhenLastExpression ?? pouch.roundUnitWhenLastExpression,
  };
}

/** レイヤーごとの roundUnit（未設定は undefined） */
export function getPouchLayerRoundUnit(pouch: Pouch, layerId: string): number | undefined {
  const entry = getLayerEntry(pouch, layerId);
  if (entry.roundUnit !== undefined) {
    return entry.roundUnit > 0 ? entry.roundUnit : undefined;
  }
  if (!pouch.layerValues || Object.keys(pouch.layerValues).length === 0) {
    const top = pouch.roundUnit;
    return top !== undefined && top > 0 ? top : undefined;
  }
  return undefined;
}

export function getPouchLayerRoundUnitWhenLastExpression(
  pouch: Pouch,
  layerId: string,
): number | undefined {
  const entry = getLayerEntry(pouch, layerId);
  if (entry.roundUnitWhenLastExpression !== undefined) {
    return entry.roundUnitWhenLastExpression;
  }
  if (!pouch.layerValues || Object.keys(pouch.layerValues).length === 0) {
    return pouch.roundUnitWhenLastExpression;
  }
  return undefined;
}

export function isPouchExpressionOutdatedForLayer(pouch: Pouch, layerId: string): boolean {
  const { lastExpression, valueWhenLastExpression, multiplyWhenLastExpression, roundUnitWhenLastExpression } =
    getPouchLayerExpressionFields(pouch, layerId);
  if (!lastExpression?.trim() || valueWhenLastExpression == null) {
    return false;
  }
  const v = getLayerValue(pouch, layerId);
  const roundMismatch =
    (getPouchLayerRoundUnit(pouch, layerId) ?? undefined) !==
    (roundUnitWhenLastExpression ?? undefined);

  if (getPouchLayerIsTime(pouch, layerId)) {
    if (multiplyWhenLastExpression == null) {
      return v !== valueWhenLastExpression || roundMismatch;
    }
    const currentMult = getPouchLayerMultiplier(pouch, layerId);
    return (
      v !== valueWhenLastExpression ||
      currentMult !== multiplyWhenLastExpression ||
      roundMismatch
    );
  }

  if (multiplyWhenLastExpression == null) {
    return false;
  }
  return (
    v !== valueWhenLastExpression ||
    pouch.multiplier !== multiplyWhenLastExpression ||
    roundMismatch
  );
}

/** 保存済み数式文字列（layerValues 経路を含む） */
export function getPouchStoredExpression(
  pouch: Pouch,
  sheet: Sheet | undefined,
  layerId?: string,
): string {
  const lid = layerId ?? (sheet ? resolveActiveLayerId(sheet) : DEFAULT_LAYER_ID);
  if (sheet && pouchUsesLayerValues(sheet, pouch)) {
    return getPouchLayerExpressionFields(pouch, lid).lastExpression?.trim() ?? '';
  }
  return pouch.lastExpression?.trim() ?? '';
}

/** 数式記録時と現在値がずれたか（layerValues 経路を含む） */
export function isPouchExpressionOutdatedForSheet(
  pouch: Pouch,
  sheet: Sheet | undefined,
  layerId?: string,
): boolean {
  const lid = layerId ?? (sheet ? resolveActiveLayerId(sheet) : DEFAULT_LAYER_ID);
  if (sheet && pouchUsesLayerValues(sheet, pouch)) {
    return isPouchExpressionOutdatedForLayer(pouch, lid);
  }
  if (
    !pouch.lastExpression?.trim() ||
    pouch.valueWhenLastExpression == null ||
    pouch.multiplyWhenLastExpression == null
  ) {
    return false;
  }
  const currentMult = getPouchEffectiveMultiplierForSheet(pouch, sheet, lid);
  return (
    pouch.value !== pouch.valueWhenLastExpression ||
    currentMult !== pouch.multiplyWhenLastExpression ||
    (pouch.roundUnit ?? undefined) !== (pouch.roundUnitWhenLastExpression ?? undefined)
  );
}

/** 編集・増減ボタン用: アクティブレイヤーの数値（layerValues を持つ場合は layerValues を正とする） */
export function getPouchDisplayValueForEdit(pouch: Pouch, sheet: Sheet | undefined): number {
  if (sheet && pouchUsesLayerValues(sheet, pouch)) {
    return getLayerValue(pouch, resolveActiveLayerId(sheet));
  }
  return pouch.value;
}

export function getPouchEditInitialString(pouch: Pouch, sheet: Sheet | undefined, layerId?: string): string {
  const lid = layerId ?? (sheet ? resolveActiveLayerId(sheet) : DEFAULT_LAYER_ID);
  if (sheet && pouchUsesLayerValues(sheet, pouch)) {
    const expr = getPouchLayerExpressionFields(pouch, lid);
    if (expr.lastExpression?.trim() && !isPouchExpressionOutdatedForLayer(pouch, lid)) {
      return expr.lastExpression;
    }
    return getLayerValue(pouch, lid).toString();
  }
  if (
    pouch.lastExpression?.trim() &&
    pouch.valueWhenLastExpression != null &&
    pouch.multiplyWhenLastExpression != null &&
    pouch.value === pouch.valueWhenLastExpression &&
    pouch.multiplier === pouch.multiplyWhenLastExpression &&
    (getPouchLayerRoundUnit(pouch, lid) ?? undefined) ===
      (getPouchLayerRoundUnitWhenLastExpression(pouch, lid) ?? undefined)
  ) {
    return pouch.lastExpression;
  }
  return pouch.value.toString();
}

function computeGtpValueForMirroredLayer(
  pouch: Pouch,
  mirroredBase: Pouch[],
  groups: Group[]
): number {
  const base = getGroupTotalPouchDisplayBase(pouch, groups, mirroredBase);
  return pouch.oppositeToTotal ? -base : base;
}

/**
 * アクティブレイヤーで集計・表示するパウチ一覧。
 * GTP は layerValues の固定値ではなく参照グループ小計をその場で求める（非 primary レイヤーが 0 初期化でも計上される）。
 */
/** layerValues が残っている単層シート（レイヤー削除後など）もミラー対象 */
function sheetNeedsLayerMirroring(sheet: Sheet, pouches: Pouch[]): boolean {
  if (!sheet.layers?.length) return false;
  if (sheetHasMultipleLayers(sheet)) return true;
  return pouches.some(
    p => p.layerValues != null && Object.keys(p.layerValues).length > 0
  );
}

export function mirrorPouchesForLayer(
  pouches: Pouch[],
  layerId: string,
  sheet: Sheet,
  groups: Group[]
): Pouch[] {
  if (!sheetNeedsLayerMirroring(sheet, pouches)) {
    return pouches;
  }
  const base = pouches.map(p => pouchWithLayerValue(p, layerId, sheet));
  return base.map(p => {
    if (!p.isGroupTotalPouch || !p.referencedGroupId) {
      return p;
    }
    const value = computeGtpValueForMirroredLayer(p, base, groups);
    return value === p.value ? p : { ...p, value };
  });
}

/** sheet.layers に無いレイヤー ID を layerValues から除去 */
export function prunePouchLayerValuesForSheet(sheet: Sheet, pouches: Pouch[]): Pouch[] {
  const allowed = new Set(getSortedLayers(sheet).map(l => l.id));
  return pouches.map(p => {
    if (!p.layerValues) return p;
    const rest = Object.fromEntries(
      Object.entries(p.layerValues).filter(([id]) => allowed.has(id))
    );
    if (Object.keys(rest).length === Object.keys(p.layerValues).length) return p;
    return {
      ...p,
      layerValues: Object.keys(rest).length ? rest : undefined,
    };
  });
}

/**
 * API write 等で旧形式の scalar だけが渡されたとき、表示ミラー値を activeLayer に取り込む。
 * layerValues[activeLayer] が明示されている場合は layerValues を正として扱い、表示ミラー値で潰さない。
 */
export function syncIncomingPouchScalarsToActiveLayer(sheet: Sheet): Sheet {
  if (!sheet.layers?.length) return sheet;
  const layerId = resolveActiveLayerId(sheet);
  const pouches = sheet.pouches.map(p => {
    const entry = p.layerValues?.[layerId];
    if (!entry) {
      const extras: Partial<PouchLayerValue> = {};
      if (p.valueWhenLastExpression !== undefined) {
        extras.valueWhenLastExpression = p.valueWhenLastExpression;
      }
      return setPouchLayerValue(p, layerId, p.value, extras, sheet);
    }
    return p;
  });
  return { ...sheet, pouches };
}

/** 全パウチのスカラー表示フィールドを activeLayer の layerValues に同期 */
export function syncSheetPouchesToActiveLayer(sheet: Sheet): Sheet {
  if (!sheet.layers?.length) return sheet;
  const layerId = resolveActiveLayerId(sheet);
  const groups = sheet.groups ?? [];
  const pruned = prunePouchLayerValuesForSheet(sheet, sheet.pouches);
  if (!sheetNeedsLayerMirroring(sheet, pruned)) {
    return { ...sheet, pouches: pruned };
  }

  // GTP 値だけ read-only mirror で再計算（persist 時は multiplier を汚さない）
  const mirroredForCalc = mirrorPouchesForLayer(pruned, layerId, sheet, groups);
  const gtpValues = new Map(
    mirroredForCalc
      .filter(p => p.isGroupTotalPouch && p.referencedGroupId)
      .map(p => [p.id, p.value] as const)
  );

  const pouches = pruned.map(p => {
    let synced = syncPouchValueMirror(p, layerId, sheet);
    const gtpValue = gtpValues.get(p.id);
    if (gtpValue !== undefined && gtpValue !== synced.value) {
      synced = setPouchLayerValue(synced, layerId, gtpValue, undefined, sheet);
    }
    return synced;
  });
  return { ...sheet, pouches };
}

/** レイヤーごとの時刻表示フラグ（未設定は非時刻。レガシー単層は pouch.isTime） */
export function getPouchLayerIsTime(pouch: Pouch, layerId: string, sheet?: Sheet): boolean {
  const entry = pouch.layerValues?.[layerId];
  if (entry) {
    if (entry.isTime === true) return true;
    if (entry.isTime === false) return false;
    if (sheet) {
      const layer = sheet.layers?.find(l => l.id === layerId);
      if (layer && sheetLayerIsTimeKind(layer)) return true;
    }
  }
  if (!pouch.layerValues || Object.keys(pouch.layerValues).length === 0) {
    return pouch.isTime === true;
  }
  return false;
}

function leafIsTimeMemberOnLayer(p: Pouch, layerId: string, sheet: Sheet): boolean {
  const layer = sheet.layers?.find(l => l.id === layerId);
  const entry = p.layerValues?.[layerId];
  if (layer && sheetLayerIsTimeKind(layer)) {
    if (entry) {
      if (entry.isTime === false) return false;
      return true;
    }
    return p.isTime === true;
  }
  if (entry) {
    if (entry.isTime === true) return true;
    if (entry.isTime === false) return p.isTime === true;
  }
  return getPouchLayerIsTime(p, layerId, sheet);
}

function groupMembersAllTimeOnLayer(
  members: Pouch[],
  layerId: string,
  sheet: Sheet,
): boolean {
  if (members.length === 0) return false;
  return members.every(p => leafIsTimeMemberOnLayer(p, layerId, sheet));
}

/** 時間メンバーだけのグループ: レイヤー上の実効分数合計 */
function sumGroupTimeMinutesOnLayer(
  members: Pouch[],
  layerId: string,
  sheet: Sheet,
): number {
  return members.reduce(
    (acc, p) => acc + getPouchTimeDisplayMinutes(p, sheet, layerId),
    0,
  );
}

/** スカラーマージ／グループ合計化: 構成パウチがすべて時間か */
export function allScalarMergeLeavesAreTimePouches(leaves: Pouch[], sheet?: Sheet): boolean {
  if (leaves.length === 0) return false;
  if (!sheet?.layers?.length) {
    return leaves.every(p => p.isTime === true);
  }
  const timeLayers = getSortedLayers(sheet).filter(l => sheetLayerIsTimeKind(l));
  if (timeLayers.length === 0) {
    return leaves.every(p => p.isTime === true);
  }
  return leaves.every(p =>
    timeLayers.every(tl => leafIsTimeMemberOnLayer(p, tl.id, sheet)),
  );
}

/** レイヤー切替が値↔時間を跨ぐか */
export function pouchLayerCrossesTimeBoundary(
  pouch: Pouch,
  fromLayerId: string,
  toLayerId: string,
): boolean {
  return getPouchLayerIsTime(pouch, fromLayerId) !== getPouchLayerIsTime(pouch, toLayerId);
}

/** multiplier（×N・60）の縦スライド: 値↔値のみ無効、時間レイヤー絡みは有効 */
export function pouchLayerShouldSlideMultiplier(
  pouch: Pouch,
  fromLayerId: string,
  toLayerId: string,
): boolean {
  return (
    getPouchLayerIsTime(pouch, fromLayerId) || getPouchLayerIsTime(pouch, toLayerId)
  );
}

/** レイヤーごとの multiplier（時間レイヤーのみ layerValues。それ以外は pouch.multiplier） */
export function getPouchLayerMultiplier(pouch: Pouch, layerId: string): number {
  if (getPouchLayerIsTime(pouch, layerId)) {
    const entry = getLayerEntry(pouch, layerId);
    if (entry.multiplier != null) return entry.multiplier;
    const layerCount = pouch.layerValues ? Object.keys(pouch.layerValues).length : 0;
    if (layerCount > 1) return 1;
    return pouch.multiplier;
  }
  return pouch.multiplier;
}

export function pouchWithLayerValue(pouch: Pouch, layerId: string, sheet?: Sheet): Pouch {
  const entry = getLayerEntry(pouch, layerId);
  return {
    ...pouch,
    value: entry.value,
    lastExpression: entry.lastExpression,
    valueWhenLastExpression: entry.valueWhenLastExpression,
    isTime: getPouchLayerIsTime(pouch, layerId),
    multiplier: getPouchLayerMultiplier(pouch, layerId),
    roundUnit: getPouchLayerRoundUnit(pouch, layerId),
    roundUnitWhenLastExpression: getPouchLayerRoundUnitWhenLastExpression(pouch, layerId),
  };
}

/** 多層時: 指定レイヤーの roundUnit を更新しミラー */
export function applyPouchRoundUnitForLayer(
  pouch: Pouch,
  layerId: string,
  roundUnit: number | undefined,
  extras?: {
    roundUnitWhenLastExpression?: number | undefined;
    clearExpression?: boolean;
  },
  sheet?: Sheet,
): Pouch {
  const layerValues = { ...(pouch.layerValues ?? {}) };
  const entry = getLayerEntry(pouch, layerId);
  const nextRu = roundUnit !== undefined && roundUnit > 0 ? roundUnit : undefined;
  layerValues[layerId] = {
    ...entry,
    roundUnit: nextRu,
    ...(extras?.roundUnitWhenLastExpression !== undefined
      ? { roundUnitWhenLastExpression: extras.roundUnitWhenLastExpression }
      : extras?.clearExpression
        ? { roundUnitWhenLastExpression: undefined }
        : {}),
  };
  let next: Pouch = { ...pouch, layerValues };
  if (extras?.clearExpression) {
    next = {
      ...next,
      lastExpression: '',
      valueWhenLastExpression: undefined,
      multiplyWhenLastExpression: undefined,
      roundUnitWhenLastExpression: undefined,
    };
  }
  return syncPouchValueMirror(next, layerId, sheet);
}

export function syncPouchValueMirror(pouch: Pouch, layerId: string, sheet?: Sheet): Pouch {
  const mirrored = pouchWithLayerValue(pouch, layerId, sheet);
  // 時間レイヤー表示中も pouch.multiplier は数量レイヤー共通値を保持（時間は layerValues.multiplier）
  if (getPouchLayerIsTime(pouch, layerId)) {
    return { ...mirrored, multiplier: pouch.multiplier };
  }
  return mirrored;
}

/** 時間葉のマージ後: 時間 layerValues を top-level に同期（active が数量でも value=60×mult にする） */
export function syncMergedTimePouchTopLevel(pouch: Pouch, sheet: Sheet): Pouch {
  const timeLayerId = getPrimaryTimeLayerId(sheet);
  if (timeLayerId && getPouchLayerIsTime(pouch, timeLayerId, sheet)) {
    return syncPouchValueMirror({ ...pouch, isTime: true }, timeLayerId, sheet);
  }
  const activeId = resolveActiveLayerId(sheet);
  if (getPouchLayerIsTime(pouch, activeId, sheet)) {
    return syncPouchValueMirror({ ...pouch, isTime: true }, activeId, sheet);
  }
  if (pouch.isTime === true && pouch.layerValues) {
    const bakeActiveId = resolveActiveLayerId(sheet);
    const entry = getLayerEntry(pouch, bakeActiveId);
    const effectiveMinutes = Math.round(
      entry.isTime === true
        ? getTimeLayerEffectiveMinutes(entry, pouch.multiplier)
        : entry.value,
    );
    const baked = timePouchStateFromEffectiveMinutes(effectiveMinutes);
    const withEntry = setPouchLayerValue(
      pouch,
      bakeActiveId,
      baked.value,
      { multiplier: baked.multiplier, isTime: true },
      sheet,
    );
    return syncPouchValueMirror({ ...withEntry, isTime: true }, bakeActiveId, sheet);
  }
  return pouch;
}

export function setPouchLayerValue(
  pouch: Pouch,
  layerId: string,
  value: number,
  extras?: Partial<PouchLayerValue>,
  sheet?: Sheet
): Pouch {
  const layerValues = { ...(pouch.layerValues ?? {}) };
  layerValues[layerId] = {
    ...getLayerEntry(pouch, layerId),
    value,
    ...extras,
  };
  return syncPouchValueMirror({ ...pouch, layerValues }, layerId, sheet);
}

/** layerValues の深いコピー（シート複製・パウチ複製用） */
export function clonePouchLayerValues(
  layerValues: Pouch['layerValues']
): Pouch['layerValues'] | undefined {
  if (!layerValues) return undefined;
  return Object.fromEntries(
    Object.entries(layerValues).map(([layerId, entry]) => [layerId, { ...entry }])
  );
}

/**
 * スカラー値・マルチを反映（多層時は activeLayer の layerValues を更新）
 */
export function applyPouchScalarForSheet(
  pouch: Pouch,
  value: number,
  sheet: Sheet,
  options?: { multiplier?: number; clearExpression?: boolean }
): Pouch {
  const multiplier = options?.multiplier ?? pouch.multiplier;
  const exprExtras: Partial<PouchLayerValue> | undefined = options?.clearExpression
    ? { lastExpression: '', valueWhenLastExpression: undefined }
    : undefined;

  if (sheet.layers?.length) {
    const lid = resolveActiveLayerId(sheet);
    const isTimeLayer = getPouchLayerIsTime(pouch, lid);
    const multForSign = options?.multiplier ?? getPouchLayerMultiplier(pouch, lid);
    const isNegative =
      isTimeLayer && !pouch.isGroupTotalPouch
        ? value * multForSign < 0
        : value < 0;
    const layerMultExtras: Partial<PouchLayerValue> = {
      ...exprExtras,
      ...(getPouchLayerIsTime(pouch, lid) && options?.multiplier !== undefined
        ? { multiplier: options.multiplier }
        : {}),
    };
    const pouchPatch =
      getPouchLayerIsTime(pouch, lid) && options?.multiplier !== undefined
        ? { ...pouch, isNegative }
        : { ...pouch, multiplier, isNegative };
    return setPouchLayerValue(pouchPatch, lid, value, layerMultExtras, sheet);
  }

  const isNegative =
    pouch.isTime && !pouch.isGroupTotalPouch
      ? value * multiplier < 0
      : value < 0;

  return {
    ...pouch,
    value,
    multiplier,
    isNegative,
    ...(options?.clearExpression
      ? { lastExpression: '', valueWhenLastExpression: undefined }
      : {}),
  };
}

/** レイヤー未搭載時代のパウチ（value のみ / layerValues 空） */
function isLegacyScalarPouch(pouch: Pouch, layers: SheetLayer[]): boolean {
  if (pouch.layerValues == null) return true;
  return !layers.some(layer => pouch.layerValues![layer.id] != null);
}

export function ensurePouchLayerValues(pouch: Pouch, sheet: Sheet): Pouch {
  const layers = getSortedLayers(sheet);
  const primaryLayerId = getPrimaryLayer(sheet).id;
  const layerValues = { ...(pouch.layerValues ?? {}) };
  const legacyScalars = isLegacyScalarPouch(pouch, layers);
  let changed = false;

  // 旧 default キーのみあるデータを primary（レイヤー1）へ移す
  if (
    !legacyScalars &&
    layerValues[DEFAULT_LAYER_ID] &&
    primaryLayerId !== DEFAULT_LAYER_ID &&
    layerValues[primaryLayerId] == null
  ) {
    layerValues[primaryLayerId] = { ...layerValues[DEFAULT_LAYER_ID] };
    changed = true;
  }

  for (const layer of layers) {
    const isPrimary = layer.id === primaryLayerId;
    if (!layerValues[layer.id]) {
      layerValues[layer.id] = {
        value: legacyScalars && isPrimary ? pouch.value : 0,
        lastExpression: legacyScalars && isPrimary ? pouch.lastExpression : undefined,
        valueWhenLastExpression:
          legacyScalars && isPrimary ? pouch.valueWhenLastExpression : undefined,
        isTime:
          legacyScalars && isPrimary
            ? pouch.isTime === true
            : false,
        roundUnit:
          legacyScalars && isPrimary && pouch.roundUnit !== undefined && pouch.roundUnit > 0
            ? pouch.roundUnit
            : undefined,
        roundUnitWhenLastExpression:
          legacyScalars && isPrimary ? pouch.roundUnitWhenLastExpression : undefined,
        multiplier:
          legacyScalars && isPrimary && pouch.isTime === true ? pouch.multiplier : undefined,
      };
      changed = true;
    } else if (
      legacyScalars &&
      isPrimary &&
      layerValues[layer.id].roundUnit == null &&
      pouch.roundUnit !== undefined &&
      pouch.roundUnit > 0
    ) {
      layerValues[layer.id] = {
        ...layerValues[layer.id],
        roundUnit: pouch.roundUnit,
        roundUnitWhenLastExpression:
          layerValues[layer.id].roundUnitWhenLastExpression ?? pouch.roundUnitWhenLastExpression,
      };
      changed = true;
    } else if (
      getPouchLayerIsTime({ ...pouch, layerValues }, layer.id) &&
      layerValues[layer.id].multiplier == null
    ) {
      layerValues[layer.id] = {
        ...layerValues[layer.id],
        multiplier: layers.length > 1 ? 1 : pouch.multiplier,
      };
      changed = true;
    } else if (layerValues[layer.id].isTime == null) {
      const entry = layerValues[layer.id];
      const inferred =
        legacyScalars && isPrimary
          ? pouch.isTime === true
          : layer.kind === 'time' &&
              Math.abs(entry.value) === 60 &&
              pouch.multiplier !== 1;
      layerValues[layer.id] = { ...entry, isTime: inferred };
      changed = true;
    }
  }

  if (legacyScalars || !pouch.layerValues) {
    changed = true;
  }

  if (!changed) return pouch;
  const activeId = resolveActiveLayerId(sheet);
  return syncPouchValueMirror({ ...pouch, layerValues }, activeId, sheet);
}

/** 履歴エントリ時点のレイヤー一覧（entry.layers 優先。無い場合は当時の layerValues キーのみ） */
export function layersForHistoryEntryContext(
  entry: SheetHistoryEntry,
  currentLayers: SheetLayer[]
): SheetLayer[] {
  if (entry.layers?.length) {
    return entry.layers.map(l => ({ ...l }));
  }
  const sorted =
    currentLayers.length > 0 ? [...currentLayers].sort((a, b) => a.order - b.order) : [createDefaultLayer()];
  // 汚染済み layerValues を信頼しない（INIT は常に primary のみ）
  if (entry.type === HistoryActionType.INIT) {
    const primary = sorted.find(l => l.primary) ?? sorted[0];
    return [{ ...primary }];
  }
  const layerIds = new Set<string>();
  for (const p of entry.pouches ?? []) {
    if (p.layerValues) {
      for (const id of Object.keys(p.layerValues)) {
        layerIds.add(id);
      }
    }
  }
  if (layerIds.size === 0) {
    const primary = sorted.find(l => l.primary) ?? sorted[0];
    return [{ ...primary }];
  }
  const filtered = sorted.filter(l => layerIds.has(l.id));
  return (filtered.length ? filtered : [sorted[0]]).map(l => ({ ...l }));
}

export function migrateSheetLayers(sheet: Sheet): Sheet {
  let layers: SheetLayer[] = sheet.layers?.length
    ? [...sheet.layers]
    : [
        {
          ...createDefaultLayer(),
          kind: sheet.pouches?.some(p => p.isTime) ? 'time' : 'number',
        },
      ];
  const activeLayerId = resolveActiveLayerId({ ...sheet, layers });
  let balancingTargets = sheet.balancingTargets ? { ...sheet.balancingTargets } : undefined;
  // レガシー balancingTargetTotal は primary レイヤー用（active と無関係に誤割当しない）
  if (sheet.balancingTargetTotal != null && balancingTargets == null) {
    const primaryId = getPrimaryLayer({ ...sheet, layers, activeLayerId }).id;
    balancingTargets = { [primaryId]: sheet.balancingTargetTotal };
  }
  const sheetCtx: Sheet = { ...sheet, layers, activeLayerId, balancingTargets };
  const migratePouches = (pouches: Pouch[]) =>
    pouches.map(p => ensurePouchLayerValues(p, sheetCtx));

  return {
    ...sheetCtx,
    pouches: migratePouches(sheet.pouches ?? []),
    history: (sheet.history ?? []).map(entry => {
      const entryLayers = layersForHistoryEntryContext(entry, layers);
      const entryActiveId =
        entry.activeLayerId && entryLayers.some(l => l.id === entry.activeLayerId)
          ? entry.activeLayerId
          : resolveActiveLayerId({ ...sheetCtx, layers: entryLayers });
      const entryCtx: Sheet = { ...sheetCtx, layers: entryLayers, activeLayerId: entryActiveId };
      const migrateEntryPouches = (ps: Pouch[]) =>
        ps.map(p => ensurePouchLayerValues(p, entryCtx));
      return {
        ...entry,
        layers: entry.layers?.length ? entry.layers.map(l => ({ ...l })) : entryLayers.map(l => ({ ...l })),
        activeLayerId: entryActiveId,
        pouches: migrateEntryPouches(entry.pouches ?? []),
      };
    }),
    snapshots: (sheet.snapshots ?? []).map(snapshot => ({
      ...snapshot,
      pouches: migratePouches(snapshot.pouches ?? []),
    })),
  };
}

export function compareLayerOrder(sheet: Sheet, oldLayerId: string, newLayerId: string): 1 | -1 | 0 {
  const sorted = getSortedLayers(sheet);
  const oldIdx = sorted.findIndex(l => l.id === oldLayerId);
  const newIdx = sorted.findIndex(l => l.id === newLayerId);
  if (oldIdx < 0 || newIdx < 0 || oldIdx === newIdx) return 0;
  return newIdx > oldIdx ? 1 : -1;
}

/** 保存名が空（createDefaultLayer 直後など。表示は layer.defaultName） */
export function isLayerStoredNameDefault(layer: SheetLayer): boolean {
  return !layer.name?.trim();
}

/** 1層かつ保存名が初期値のとき、操作アイドルでレイヤー行を隠す */
export function shouldHideLayerChipWhenIdle(sheet: Sheet): boolean {
  const layers = getSortedLayers(sheet);
  return layers.length === 1 && isLayerStoredNameDefault(layers[0]);
}

/** 表示名（空名はロケールの layer.defaultName と同じ扱い） */
export function layerDisplayName(layer: SheetLayer): string {
  const trimmed = layer.name?.trim();
  return trimmed ? trimmed : getAppCopy().layer.defaultName;
}

/** 新規レイヤー用の既定名: 値 → 値 2 …（ロケールに応じた base + スペース + 番号） */
export function nextDefaultLayerName(sheet: Sheet): string {
  const base = getAppCopy().layer.defaultName;
  const taken = new Set(getSortedLayers(sheet).map(layerDisplayName));
  if (!taken.has(base)) return base;
  const isTaken = (n: number) => taken.has(`${base} ${n}`) || taken.has(`${base}${n}`);
  let n = 2;
  while (isTaken(n)) n++;
  return `${base} ${n}`;
}

export function addSheetLayer(sheet: Sheet, name?: string): Sheet {
  const sorted = getSortedLayers(sheet);
  const sourceLayerId = resolveActiveLayerId(sheet);
  const sourceLayer = getLayerById(sheet, sourceLayerId);
  const maxOrder = sorted.reduce((m, l) => Math.max(m, l.order), -1);
  const newLayer: SheetLayer = {
    id: uuidv4(),
    name: name ?? nextDefaultLayerName(sheet),
    order: maxOrder + 1,
    kind: sourceLayer?.kind ?? 'number',
    ...(sourceLayer?.unit ? { unit: sourceLayer.unit } : {}),
  };
  const layers = [...sorted, newLayer];
  const sheetCtx: Sheet = { ...sheet, layers, activeLayerId: newLayer.id };
  const sourceBalancingTarget = getBalancingTargetForLayer(sheet, sourceLayerId);
  const balancingTargets =
    sourceBalancingTarget !== undefined
      ? { ...(sheet.balancingTargets ?? {}), [newLayer.id]: sourceBalancingTarget }
      : sheet.balancingTargets
        ? { ...sheet.balancingTargets }
        : undefined;
  const sheetWithBalancing: Sheet = { ...sheetCtx, balancingTargets };
  const pouches = sheet.pouches.map(p => {
    const ensured = ensurePouchLayerValues(p, sheetWithBalancing);
    const sourceEntry = getLayerEntry(ensured, sourceLayerId);
    const layerValues = { ...(ensured.layerValues ?? {}) };
    layerValues[newLayer.id] = { ...sourceEntry };
    return syncPouchValueMirror({ ...ensured, layerValues }, newLayer.id, sheetWithBalancing);
  });
  return migrateSheetLayers({
    ...sheetWithBalancing,
    pouches,
  });
}

export function updateSheetLayerName(sheet: Sheet, layerId: string, name: string): Sheet {
  const layers = getSortedLayers(sheet).map(l =>
    l.id === layerId ? { ...l, name } : l
  );
  return { ...sheet, layers };
}

export function removeSheetLayer(sheet: Sheet, layerId: string): Sheet {
  const sorted = getSortedLayers(sheet);
  if (sorted.length <= 1) return sheet;
  const removedIndex = sorted.findIndex(l => l.id === layerId);
  const layers = sorted.filter(l => l.id !== layerId);
  if (!layers.some(l => l.primary)) {
    layers[0] = { ...layers[0], primary: true };
  }
  const nextActiveId =
    sheet.activeLayerId === layerId
      ? layers[removedIndex <= 0 ? 0 : removedIndex - 1].id
      : resolveActiveLayerId({ ...sheet, layers, activeLayerId: sheet.activeLayerId });
  const pouches = sheet.pouches.map(p => {
    const rest = { ...(p.layerValues ?? {}) };
    delete rest[layerId];
    return ensurePouchLayerValues(
      { ...p, layerValues: Object.keys(rest).length ? rest : undefined },
      { ...sheet, layers, activeLayerId: nextActiveId }
    );
  });
  const balancingTargets = sheet.balancingTargets ? { ...sheet.balancingTargets } : undefined;
  if (balancingTargets) {
    delete balancingTargets[layerId];
  }
  return migrateSheetLayers({
    ...sheet,
    layers,
    activeLayerId: nextActiveId,
    pouches,
    balancingTargets,
  });
}

export function setSheetActiveLayer(
  sheet: Sheet,
  newLayerId: string
): { sheet: Sheet; direction: 1 | -1 | 0 } {
  const oldId = resolveActiveLayerId(sheet);
  const direction = compareLayerOrder(sheet, oldId, newLayerId);
  const next = migrateSheetLayers({ ...sheet, activeLayerId: newLayerId });
  return { sheet: next, direction };
}

/** 指定レイヤーの葉パウチ合計（BP 目標のスナップショット用） */
export function calculateSheetTotalForLayer(
  pouches: Pouch[],
  groups: Group[],
  layerId: string,
  sheet: Sheet
): number {
  const mirrored = mirrorPouchesForLayer(pouches, layerId, sheet, groups);
  return calculateSheetTotal(mirrored, groups);
}

export function getBalancingTargetForLayer(sheet: Sheet, layerId?: string): number | undefined {
  const lid = layerId ?? resolveActiveLayerId(sheet);
  if (sheet.balancingTargets && Object.prototype.hasOwnProperty.call(sheet.balancingTargets, lid)) {
    return sheet.balancingTargets[lid];
  }
  // per-layer 未導入のレガシーシートのみ（balancingTargets が無いときだけ total を使う）
  if (!sheet.balancingTargets && sheet.balancingTargetTotal != null) {
    return sheet.balancingTargetTotal;
  }
  return undefined;
}

/**
 * BP オン時: レイヤーに目標がなければそのレイヤーの葉合計を目標として記録する。
 * （他レイヤーで BP 済みのあと、未設定レイヤーへ切り替えた／編集したとき用）
 */
export function ensureBalancingTargetForLayer(
  sheet: Sheet,
  layerId: string,
  pouches: Pouch[],
  groups: Group[]
): { sheet: Sheet; target: number | undefined } {
  if (!sheet.balancingPouchId) {
    return { sheet, target: undefined };
  }
  const existing = getBalancingTargetForLayer(sheet, layerId);
  if (existing !== undefined) {
    return { sheet, target: existing };
  }
  const target = calculateSheetTotalForLayer(pouches, groups, layerId, sheet);
  return { sheet: withBalancingTargetForLayer(sheet, layerId, target), target };
}

/** UI 用 legacy フィールドをアクティブレイヤーの目標に合わせる（他レイヤーの target が残らないように） */
export function syncBalancingTargetTotalToActiveLayer(sheet: Sheet): Sheet {
  const mirrored = syncSheetPouchesToActiveLayer(sheet);
  const activeId = resolveActiveLayerId(mirrored);
  const target = getBalancingTargetForLayer(mirrored, activeId);
  return {
    ...mirrored,
    balancingTargetTotal: target,
  };
}

/** BP 調整後の pouch.value をアクティブレイヤーの layerValues に反映（表示・レイヤー切替用） */
export function syncBalancingPouchToActiveLayer(
  pouches: Pouch[],
  balancingPouchId: string | null | undefined,
  sheet: Sheet
): Pouch[] {
  if (!balancingPouchId) return pouches;
  const layerId = resolveActiveLayerId(sheet);
  return pouches.map(p => {
    if (p.id !== balancingPouchId) return p;
    return setPouchLayerValue(
      p,
      layerId,
      p.value,
      {
        lastExpression: p.lastExpression,
        valueWhenLastExpression: p.valueWhenLastExpression,
      },
      sheet
    );
  });
}

export function withBalancingTargetForLayer(
  sheet: Sheet,
  layerId: string,
  target: number | undefined
): Sheet {
  const balancingTargets = { ...(sheet.balancingTargets ?? {}) };
  if (target === undefined) {
    delete balancingTargets[layerId];
  } else {
    balancingTargets[layerId] = target;
  }
  const activeId = resolveActiveLayerId(sheet);
  return {
    ...sheet,
    balancingTargets,
    balancingTargetTotal: layerId === activeId ? target : sheet.balancingTargetTotal,
  };
}

/** 掛け算/割り算の実効値（キャンバス上のパウチマージ用） */
export function getPouchMergeEffectiveValue(value: number, multiplier: number): number {
  if (multiplier === 0) return 0;
  return multiplier > 0 ? value * multiplier : value / Math.abs(multiplier);
}

export type PouchScalarMergeResult = { value: number; multiplier: number };

/**
 * 2パウチを全レイヤーでマルチ合成できるか（各レイヤーで |値| が一致）。
 * 1つでも違えば値合成（実効値の和 → 値、マルチ1）にする。
 */
export function canMergePouchesWithMultiplier(
  targetPouch: Pouch,
  mergeCandidate: Pouch,
  sheet: Sheet | undefined
): boolean {
  if (!sheet?.layers?.length) {
    return Math.abs(targetPouch.value) === Math.abs(mergeCandidate.value);
  }
  const layerIds = new Set<string>();
  for (const layer of sheet.layers) {
    layerIds.add(layer.id);
  }
  for (const id of Object.keys(targetPouch.layerValues ?? {})) {
    layerIds.add(id);
  }
  for (const id of Object.keys(mergeCandidate.layerValues ?? {})) {
    layerIds.add(id);
  }
  return Array.from(layerIds).every(layerId => {
    const t = getLayerValue(targetPouch, layerId);
    const c = getLayerValue(mergeCandidate, layerId);
    return Math.abs(t) === Math.abs(c);
  });
}

/**
 * 2組の value/multiplier をマージ（ActiveSheet processPouchMerge と同じ規則）。
 * sameAbsValue は canMergePouchesWithMultiplier の結果を渡す。
 */
export function mergePouchScalarPair(
  targetValue: number,
  targetMultiplier: number,
  candidateValue: number,
  candidateMultiplier: number,
  sameAbsValue: boolean
): PouchScalarMergeResult {
  const targetResult = getPouchMergeEffectiveValue(targetValue, targetMultiplier);
  const candidateResult = getPouchMergeEffectiveValue(candidateValue, candidateMultiplier);
  const totalResult = targetResult + candidateResult;

  if (sameAbsValue) {
    let finalMultiplier: number;

    if (targetMultiplier === 0 || candidateMultiplier === 0) {
      if (targetMultiplier === 0 && candidateMultiplier !== 0) {
        finalMultiplier = candidateMultiplier;
      } else if (targetMultiplier !== 0 && candidateMultiplier === 0) {
        finalMultiplier = targetMultiplier;
      } else {
        finalMultiplier = 1;
      }
    } else if (Math.abs(candidateValue) === 0) {
      finalMultiplier = 1;
    } else {
      const baseValue = candidateValue;
      const mergedMultiplier = totalResult / baseValue;
      const candidateMultiplierSign = candidateMultiplier > 0 ? 1 : -1;

      if (candidateMultiplierSign > 0) {
        finalMultiplier = Math.abs(mergedMultiplier);
      } else {
        const divisionResult = 1 / Math.abs(mergedMultiplier);
        if (Number.isInteger(divisionResult)) {
          finalMultiplier = -Math.abs(divisionResult);
        } else {
          finalMultiplier = Math.abs(mergedMultiplier);
        }
      }
    }

    const value =
      Math.abs(candidateValue) === 0
        ? roundPouchValue(totalResult)
        : totalResult < 0
          ? -Math.abs(candidateValue)
          : Math.abs(candidateValue);

    return { value, multiplier: finalMultiplier };
  }

  return { value: roundPouchValue(totalResult), multiplier: 1 };
}

/** キャンバス上で2パウチを重ねたときのマージ結果（多層時はレイヤーごとに値を合成） */
export function mergeTwoPouchesOnCanvas(
  targetPouch: Pouch,
  mergeCandidate: Pouch,
  sheet: Sheet | undefined,
  meta: { maxZIndex: number; animateMultiplier: boolean }
): Pouch {
  const useLayers = !!(sheet?.layers?.length);
  const activeId = sheet ? resolveActiveLayerId(sheet) : DEFAULT_LAYER_ID;

  const sameAbsValue = canMergePouchesWithMultiplier(targetPouch, mergeCandidate, sheet);

  const targetRefVal = useLayers ? getLayerValue(targetPouch, activeId) : targetPouch.value;
  const candidateRefVal = useLayers ? getLayerValue(mergeCandidate, activeId) : mergeCandidate.value;

  const activeMerge = mergePouchScalarPair(
    targetRefVal,
    targetPouch.multiplier,
    candidateRefVal,
    mergeCandidate.multiplier,
    sameAbsValue
  );

  const gtpBoth =
    mergeCandidate.isGroupTotalPouch && targetPouch.isGroupTotalPouch;

  let merged: Pouch = {
    ...mergeCandidate,
    multiplier: activeMerge.multiplier,
    zIndex: meta.maxZIndex + 1,
    _animateMultiplier: meta.animateMultiplier,
    isGroupTotalPouch: gtpBoth,
    referencedGroupId: gtpBoth ? mergeCandidate.referencedGroupId : undefined,
    oppositeToTotal: gtpBoth ? mergeCandidate.oppositeToTotal : undefined,
  };

  if (!useLayers || !sheet) {
    return {
      ...merged,
      value: activeMerge.value,
      isNegative: activeMerge.value < 0,
    };
  }

  const layerIds = new Set<string>();
  for (const layer of sheet.layers!) {
    layerIds.add(layer.id);
  }
  for (const id of Object.keys(targetPouch.layerValues ?? {})) {
    layerIds.add(id);
  }
  for (const id of Object.keys(mergeCandidate.layerValues ?? {})) {
    layerIds.add(id);
  }

  const layerValues: NonNullable<Pouch['layerValues']> = { ...(mergeCandidate.layerValues ?? {}) };
  for (const layerId of Array.from(layerIds)) {
    const tVal = getLayerValue(targetPouch, layerId);
    const cVal = getLayerValue(mergeCandidate, layerId);
    const { value } = mergePouchScalarPair(
      tVal,
      targetPouch.multiplier,
      cVal,
      mergeCandidate.multiplier,
      sameAbsValue
    );
    layerValues[layerId] = {
      ...getLayerEntry(mergeCandidate, layerId),
      value,
    };
  }

  merged = { ...merged, layerValues };
  return syncPouchValueMirror(merged, activeId, sheet);
}

/** レイヤー別にミラーしたうえでグループ小計を求める */
export function calculateGroupSubtotalForLayer(
  groupId: string,
  pouches: Pouch[],
  groups: Group[],
  layerId: string,
  sheet: Sheet
): number {
  const mirrored = mirrorPouchesForLayer(pouches, layerId, sheet, groups);
  return calculateGroupSubtotal(groupId, mirrored, groups, mirrored);
}

/** グループマージ（枠削除→合計パウチ生成）用の値（全レイヤー分） */
function groupMergeSubtotalForLayer(
  groupId: string,
  pouchesInGroup: Pouch[],
  groups: Group[],
  layerId: string,
  sheet: Sheet,
): number {
  const layer = getLayerById(sheet, layerId);
  const allTimeOnLayer = groupMembersAllTimeOnLayer(pouchesInGroup, layerId, sheet);
  if (allTimeOnLayer || (layer && sheetLayerIsTimeKind(layer))) {
    return sumGroupTimeMinutesOnLayer(pouchesInGroup, layerId, sheet);
  }
  return calculateGroupSubtotalForLayer(
    groupId,
    pouchesInGroup,
    groups,
    layerId,
    sheet,
  );
}

export function buildGroupMergePouchLayerData(
  groupId: string,
  pouchesInGroup: Pouch[],
  groups: Group[],
  sheet: Sheet
): { subtotal: number; layerValues?: Pouch['layerValues'] } {
  const activeId = resolveActiveLayerId(sheet);
  const subtotal = groupMergeSubtotalForLayer(
    groupId,
    pouchesInGroup,
    groups,
    activeId,
    sheet,
  );
  if (!sheet.layers?.length) {
    return { subtotal };
  }
  const layerValues: NonNullable<Pouch['layerValues']> = {};
  for (const layer of getSortedLayers(sheet)) {
    const st = groupMergeSubtotalForLayer(
      groupId,
      pouchesInGroup,
      groups,
      layer.id,
      sheet,
    );
    const rounded = roundPouchValue(st);
    const allTimeOnLayer = groupMembersAllTimeOnLayer(pouchesInGroup, layer.id, sheet);
    if (sheetLayerIsTimeKind(layer) || allTimeOnLayer) {
      const baked = timePouchStateFromEffectiveMinutes(rounded);
      layerValues[layer.id] = {
        value: baked.value,
        multiplier: baked.multiplier,
        isTime: true,
      };
    } else {
      layerValues[layer.id] = { value: rounded, isTime: false };
    }
  }
  return { subtotal, layerValues };
}

/**
 * 内包 GTP の値/マルチレンダリング（多層時はレイヤーごとに係数を算出して layerValues を更新）。
 */
export function renderGroupInclusiveGtpForSheet(
  group: Group,
  pouches: Pouch[],
  groups: Group[],
  mode: 'value' | 'multi',
  sheet: Sheet,
  options: { commit?: boolean } = {}
): Pouch[] | null {
  if (!sheetHasMultipleLayers(sheet)) {
    return renderGroupInclusiveGtp(group, pouches, groups, mode, options);
  }

  const { commit = true } = options;
  const gtps = findGroupInclusiveGtps(group, pouches);
  if (gtps.length === 0) return null;

  const activeId = resolveActiveLayerId(sheet);
  const mirroredActive = mirrorPouchesForLayer(pouches, activeId, sheet, groups);
  if (computeGroupInclusiveGtpMergeFactor(group, mirroredActive, groups) === null) {
    return null;
  }

  const gtpIds = new Set(gtps.map(g => g.id));
  const muteGroupIds = new Set(groups.filter(g => g.color === 'mute').map(g => g.id));
  const isInMuteGroup = (pouchId: string): boolean =>
    groups.some(g => muteGroupIds.has(g.id) && g.pouches?.includes(pouchId));

  const targetIds = new Set(
    pouches
      .filter(
        p =>
          group.pouches?.includes(p.id) &&
          !p._isDeleting &&
          !p.isGroupTotalPouch &&
          !isInMuteGroup(p.id)
      )
      .map(p => p.id)
  );

  const layers = getSortedLayers(sheet);

  const updated = pouches.map(p => {
    if (gtpIds.has(p.id)) {
      if (commit) return p;
      return { ...p, _isGtpRenderPreview: true };
    }
    if (!targetIds.has(p.id)) return p;

    const layerValues: NonNullable<Pouch['layerValues']> = { ...(p.layerValues ?? {}) };
    let nextMultiplier = p.multiplier;

    for (const layer of layers) {
      const mirrored = mirrorPouchesForLayer(pouches, layer.id, sheet, groups);
      const factor = computeGroupInclusiveGtpMergeFactor(group, mirrored, groups);
      if (factor === null) continue;

      let bakeFactor = factor;
      const primaryId = getInclusiveGtpSheetPrimaryPouchId(group, mirrored);
      if (primaryId) {
        const primary = mirrored.find(pp => pp.id === primaryId);
        if (primary?.oppositeToTotal) {
          bakeFactor = Math.round(-factor * 1000000000) / 1000000000;
        }
      }

      const entry = getLayerEntry(p, layer.id);
      const val = entry.value;

      if (getPouchLayerIsTime(p, layer.id)) {
        const timeMult = entry.multiplier ?? 1;
        const beforeEff = getTimeLayerEffectiveMinutes(entry, timeMult);
        const baked = applyTimeEffectiveGtpBake(beforeEff, bakeFactor);
        layerValues[layer.id] = {
          ...entry,
          value: baked.value,
          multiplier: baked.multiplier,
          isTime: true,
        };
      } else if (mode === 'value') {
        const nextValue = roundPouchValue(val * bakeFactor);
        layerValues[layer.id] = { ...entry, value: nextValue };
      } else {
        const sign = bakeFactor === 0 ? 1 : Math.sign(bakeFactor);
        const absFactor = Math.abs(bakeFactor);
        const nextValue = Math.round(val * sign * 1000000000) / 1000000000;
        layerValues[layer.id] = { ...entry, value: nextValue };
        if (layer.id === activeId) {
          nextMultiplier = Math.round(p.multiplier * absFactor * 1000000000) / 1000000000;
        }
      }
    }

    let next: Pouch = { ...p, layerValues };
    if (mode === 'multi') {
      next = { ...next, multiplier: nextMultiplier };
    }
    next = syncPouchValueMirror(next, activeId, sheet);
    const displayVal = getLayerValue(next, activeId);
    return { ...next, isNegative: displayVal < 0 };
  });

  if (!commit) return updated;
  return updated.filter(p => !gtpIds.has(p.id));
}

export function getTableColumnLayout(layerCount: number) {
  const layerStart = 1;
  const mult = layerStart + layerCount;
  const count = mult + 1;
  const total = count + 1;
  const colCount = total + 1;
  return { layerStart, mult, count, total, colCount, layerCount };
}
